import React from 'react';
import DatePicker from 'react-datepicker';
import { formatDateToCustom } from './Overview.utils';

interface OverviewDateRangeProps {
  dateRangeMode: 'relative' | 'specific';
  handleDateRangeModeChange: (mode: 'relative' | 'specific') => void;
  relativeTimeValue: number;
  setRelativeTimeValue: (v: number) => void;
  relativeTimeUnit: 'min' | 'hours' | 'days';
  setRelativeTimeUnit: (v: 'min' | 'hours' | 'days') => void;
  startDate: Date | null;
  setStartDate: (v: Date | null) => void;
  endDate: Date | null;
  setEndDate: (v: Date | null) => void;
  DatePickereStartDateIsOpen: boolean;
  setDatePickereStartDateIsOpen: (v: boolean) => void;
  DatePickereEndDateIsOpen: boolean;
  setDatePickereEndDateIsOpen: (v: boolean) => void;
  calendarViewDate: Date;
  navigateToPreviousMonth: () => void;
  advanceCalendarByMonth: () => void;
  navigateToSpecificMonth: (month: number, year: number) => void;
}

const OverviewDateRange: React.FC<OverviewDateRangeProps> = ({
  dateRangeMode, handleDateRangeModeChange,
  relativeTimeValue, setRelativeTimeValue,
  relativeTimeUnit, setRelativeTimeUnit,
  startDate, setStartDate, endDate, setEndDate,
  DatePickereStartDateIsOpen, setDatePickereStartDateIsOpen,
  DatePickereEndDateIsOpen, setDatePickereEndDateIsOpen,
  calendarViewDate,
  navigateToPreviousMonth, advanceCalendarByMonth, navigateToSpecificMonth,
}) => {
  const renderCustomHeader = (
    { date, decreaseMonth, increaseMonth, changeYear, changeMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }: any,
    onClose: () => void,
    showCloseButton: boolean
  ) => (
    <div className="datepicker-custom-header">
      <button
        onClick={() => { decreaseMonth(); navigateToPreviousMonth(); }}
        disabled={prevMonthButtonDisabled}
        className="datepicker-nav-button"
        title="Previous month"
      >{'<'}</button>
      <div className="datepicker-selectors">
        <select
          value={date.getMonth()}
          onChange={({ target: { value } }) => {
            const month = Number.parseInt(value);
            changeMonth(month);
            navigateToSpecificMonth(month, date.getFullYear());
          }}
          className="datepicker-month-select"
        >
          {Array.from({ length: 12 }, (_, i) => (
            <option key={i} value={i}>
              {new Date(0, i).toLocaleString('default', { month: 'long' })}
            </option>
          ))}
        </select>
        <select
          value={date.getFullYear()}
          onChange={({ target: { value } }) => {
            const year = Number.parseInt(value);
            changeYear(year);
            navigateToSpecificMonth(date.getMonth(), year);
          }}
          className="datepicker-year-select"
        >
          {Array.from({ length: 10 }, (_, i) => {
            const year = new Date().getFullYear() - 5 + i;
            return (<option key={year} value={year}>{year}</option>);
          })}
        </select>
      </div>
      <button
        onClick={() => { increaseMonth(); advanceCalendarByMonth(); }}
        disabled={nextMonthButtonDisabled}
        className="datepicker-nav-button"
        title="Next month"
      >{'>'}</button>
      {showCloseButton && (
        <button onClick={(e) => { e.stopPropagation(); onClose(); }} className='date-btn-close' title="Close calendar">
          ✕
        </button>
      )}
    </div>
  );

  const getDayClassName = (date: Date, minDateOverride?: Date | null) => {
    const today = new Date();
    const minDate = minDateOverride || new Date('2020-01-01');
    const maxDate = new Date();
    const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const compareToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const compareMin = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    const compareMax = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
    const isToday = compareDate.getTime() === compareToday.getTime();
    const isStartDate = startDate && compareDate.getTime() === new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()).getTime();
    const isEndDate = endDate && compareDate.getTime() === new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()).getTime();
    const isDisabled = compareDate < compareMin || compareDate > compareMax;
    const isInRange = startDate && endDate && compareDate >= new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()) && compareDate <= new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    if (isDisabled) return 'react-datepicker__day--disabled-custom';
    if (isToday) return 'react-datepicker__day--today-custom';
    if (isStartDate || isEndDate) return 'react-datepicker__day--selected-custom';
    if (isInRange) return 'react-datepicker__day--in-range-custom';
    return null;
  };

  return (
    <div className="search-input date-range-section">
      <label className="two-line-label">Search by Date </label>
      <div className="date-range-mode">
        <label>
          <input type="radio" name="dateRangeMode" value="relative"
            checked={dateRangeMode === 'relative'}
            onChange={() => handleDateRangeModeChange('relative')} />
          Current
        </label>
        <label>
          <input type="radio" name="dateRangeMode" value="specific"
            checked={dateRangeMode === 'specific'}
            onChange={() => handleDateRangeModeChange('specific')} />
          Historical
        </label>
      </div>

      {dateRangeMode === 'relative' && (
        <div className="relative-time-options">
          <div className="relative-time-inputs">
            <label>Last</label>
            <input
              type="number"
              value={relativeTimeValue === 0 ? '' : relativeTimeValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '') {
                  setRelativeTimeValue(0);
                } else {
                  const numValue = Number.parseInt(value, 10);
                  if (!Number.isNaN(numValue)) {
                    setRelativeTimeValue(Math.min(numValue, 999));
                  }
                }
              }}
              className="relative-time-number"
              placeholder="Enter number"
              min="0"
              max="999"
            />
            <select
              value={relativeTimeUnit}
              onChange={(e) => setRelativeTimeUnit(e.target.value as 'min' | 'hours' | 'days')}
              className="relative-time-unit"
            >
              <option value="min">Minutes</option>
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </div>
          {startDate && endDate && (
            <div className="calculated-range">
              <small>
                From: {formatDateToCustom(startDate)} <br />
                To: {formatDateToCustom(endDate)}
              </small>
            </div>
          )}
        </div>
      )}

      {dateRangeMode === 'specific' && (
        <div className="date-picker">
          <DatePicker
            selected={startDate}
            onChange={(date: Date | null) => { setStartDate(date); setDatePickereStartDateIsOpen(false); }}
            selectsStart startDate={startDate} endDate={endDate}
            minDate={new Date('2020-01-01')} maxDate={new Date()}
            showTimeSelect timeFormat="HH:mm aa" timeIntervals={5}
            dateFormat="MMMM d, yyyy h:mm aa" timeCaption="time"
            isClearable placeholderText="Start Date" popperPlacement="bottom-end"
            className="search-input custom-datepicker"
            dayClassName={(date) => getDayClassName(date)}
            onClickOutside={() => setDatePickereStartDateIsOpen(false)}
            onInputClick={() => { setDatePickereStartDateIsOpen(true); setDatePickereEndDateIsOpen(false); }}
            open={DatePickereStartDateIsOpen}
            openToDate={calendarViewDate}
            renderCustomHeader={(headerProps) =>
              renderCustomHeader(headerProps, () => setDatePickereStartDateIsOpen(false), true)
            }
          />
          <DatePicker
            selected={endDate}
            onChange={(date: Date | null) => { setEndDate(date); setDatePickereEndDateIsOpen(false); }}
            selectsEnd startDate={startDate} endDate={endDate}
            minDate={startDate || new Date('2020-01-01')} maxDate={new Date()}
            showTimeSelect timeFormat="HH:mm aa" timeIntervals={5}
            dateFormat="MMMM d, yyyy h:mm aa" timeCaption="time"
            isClearable placeholderText="End Date" popperPlacement="bottom-end"
            className="search-input custom-datepicker"
            dayClassName={(date) => getDayClassName(date, startDate)}
            onClickOutside={() => setDatePickereEndDateIsOpen(false)}
            onInputClick={() => { setDatePickereEndDateIsOpen(true); setDatePickereStartDateIsOpen(false); }}
            open={DatePickereEndDateIsOpen}
            openToDate={calendarViewDate}
            renderCustomHeader={(headerProps) =>
              renderCustomHeader(headerProps, () => setDatePickereEndDateIsOpen(false), true)
            }
          />
          <label htmlFor="endDate" className="datepicker-label description"><sup></sup>(Times displayed are UTC)</label>
        </div>
      )}
    </div>
  );
};

export default OverviewDateRange;
